import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem, Select } from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import {
  EMPTY_SALES_FILTER_STATE,
  getSalesStatusOptions,
  normalizeSalesFilterState,
} from "../../utils/dashboardSalesFilterState";
import DashboardSavedFiltersPanel from "./DashboardSavedFiltersPanel";
import {
  DashboardFilterModalActions,
  DashboardFilterModalFrame,
  DashboardFilterSection,
  DashboardMultiPickList,
} from "./dashboardFilterModalUi";

function extractList(res) {
  const raw = res?.data ?? res;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.users)) return raw.users;
  return [];
}

export default function DashboardSalesFiltersModal({
  open,
  onClose,
  initialState = EMPTY_SALES_FILTER_STATE,
  onApply,
}) {
  const { t } = useTranslation("settings");
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [motives, setMotives] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusIds, setStatusIds] = useState([]);
  const [originIds, setOriginIds] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [motiveIds, setMotiveIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [availability, setAvailability] = useState("");
  const prevOpen = React.useRef(false);

  const statusOptions = useMemo(() => getSalesStatusOptions(), []);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        fetchData("/channels?limit=200", "GET"),
        fetchData("/categories?page=1&limit=200", "GET"),
        fetchData("/dashboard/vendas/lost-reasons", "GET"),
        fetchData("/users?page=1&limit=200&tipoResponsavel=humanos", "GET"),
      ]);
      const [ch, cat, mot, usr] = results.map((r) => (r.status === "fulfilled" ? r.value : null));
      setChannels(extractList(ch));
      const catPayload = cat?.data ?? cat;
      const catList = Array.isArray(catPayload)
        ? catPayload
        : Array.isArray(catPayload?.data)
          ? catPayload.data
          : Array.isArray(catPayload?.items)
            ? catPayload.items
            : [];
      setCategories(catList.filter((c) => !Number(c.id_pai || 0)));
      setMotives(extractList(mot));
      setUsers(extractList(usr));
    } catch (e) {
      console.error("Erro ao carregar opções de filtros de vendas:", e);
      setChannels([]);
      setCategories([]);
      setMotives([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return;
    }
    const opening = !prevOpen.current;
    prevOpen.current = true;
    if (opening) {
      const s = normalizeSalesFilterState(initialState);
      setStatusIds(s.ids_status);
      setOriginIds(s.ids_origens);
      setCategoryIds(s.ids_categorias);
      setMotiveIds(s.ids_motivos);
      setUserIds(s.ids_usuarios);
      setAvailability(s.availability);
      loadOptions();
    }
  }, [open, initialState, loadOptions]);

  const buildLabels = () => {
    const statuses = {};
    statusOptions.forEach((o) => {
      statuses[String(o.id)] = o.label;
    });
    const origins = {};
    channels.forEach((c) => {
      const id = Number(c?.id);
      if (!Number.isFinite(id)) return;
      origins[String(id)] = String(c?.nome || c?.name || `#${id}`);
    });
    const cats = {};
    categories.forEach((c) => {
      const id = Number(c?.id);
      if (!id) return;
      cats[String(id)] = String(c?.nome || c?.name || `#${id}`);
    });
    const motiveLabels = {};
    motives.forEach((m) => {
      const id = Number(m?.id);
      if (!id) return;
      motiveLabels[String(id)] = String(m?.nome || m?.name || `#${id}`);
    });
    const userLabels = {};
    users.forEach((u) => {
      const id = Number(u?.id);
      if (!id) return;
      userLabels[String(id)] = String(u?.nome || u?.name || u?.email || `#${id}`);
    });
    return {
      statuses,
      origins,
      categories: cats,
      subcategories: {},
      motives: motiveLabels,
      users: userLabels,
      agents: {},
    };
  };

  const handleClear = () => {
    setStatusIds([]);
    setOriginIds([]);
    setCategoryIds([]);
    setMotiveIds([]);
    setUserIds([]);
    setAvailability("");
  };

  const handleApply = () => {
    onApply?.(
      normalizeSalesFilterState({
        ids_status: statusIds,
        ids_origens: originIds,
        ids_categorias: categoryIds,
        ids_motivos: motiveIds,
        ids_usuarios: userIds,
        availability,
        labels: buildLabels(),
      })
    );
    onClose?.();
  };

  const hasActive =
    statusIds.length > 0 ||
    originIds.length > 0 ||
    categoryIds.length > 0 ||
    motiveIds.length > 0 ||
    userIds.length > 0 ||
    Boolean(availability);

  return (
    <DashboardFilterModalFrame
      open={open}
      onClose={onClose}
      icon={<ShoppingCartOutlinedIcon />}
      title={t("dashboardPage.salesFilters.title", { defaultValue: "Filtrar vendas" })}
      subtitle={t("dashboardPage.salesFilters.subtitle", {
        defaultValue: "Receita e gráficos passam a considerar só este recorte",
      })}
      loading={loading}
      footer={
        <DashboardFilterModalActions
          onClear={handleClear}
          onClose={onClose}
          onApply={handleApply}
          hasActive={hasActive}
          clearLabel={t("dashboardPage.salesFilters.clearAll", { defaultValue: "Limpar" })}
          cancelLabel={t("dashboardPage.salesFilters.cancel", { defaultValue: "Cancelar" })}
          applyLabel={t("dashboardPage.salesFilters.apply", { defaultValue: "Aplicar" })}
        />
      }
    >
      <DashboardFilterSection
        title={t("dashboardPage.salesFilters.status", { defaultValue: "Status" })}
        hint={t("dashboardPage.salesFilters.statusHint", {
          defaultValue: "Quais negócios entram no cálculo",
        })}
        selectedCount={statusIds.length}
      >
        <DashboardMultiPickList
          options={statusOptions}
          value={statusIds}
          onChange={setStatusIds}
          getLabel={(o) => o.label}
          getValue={(o) => o.id}
          searchPlaceholder={t("dashboardPage.salesFilters.searchStatus", {
            defaultValue: "Buscar status…",
          })}
          emptyLabel={t("dashboardPage.salesFilters.emptyStatus", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.salesFilters.origins", { defaultValue: "Canal" })}
        hint={t("dashboardPage.salesFilters.originsHint", {
          defaultValue: "Origem do lead da venda",
        })}
        selectedCount={originIds.length}
      >
        <DashboardMultiPickList
          options={channels}
          value={originIds}
          onChange={setOriginIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.salesFilters.searchOrigins", {
            defaultValue: "Buscar canais…",
          })}
          emptyLabel={t("dashboardPage.salesFilters.emptyOrigins", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.salesFilters.categories", { defaultValue: "Categoria" })}
        selectedCount={categoryIds.length}
      >
        <DashboardMultiPickList
          options={categories}
          value={categoryIds}
          onChange={setCategoryIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.salesFilters.searchCategories", {
            defaultValue: "Buscar categorias…",
          })}
          emptyLabel={t("dashboardPage.salesFilters.emptyCategories", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.salesFilters.motives", { defaultValue: "Motivo de perda" })}
        selectedCount={motiveIds.length}
      >
        <DashboardMultiPickList
          options={motives}
          value={motiveIds}
          onChange={setMotiveIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.salesFilters.searchMotives", {
            defaultValue: "Buscar motivos…",
          })}
          emptyLabel={t("dashboardPage.salesFilters.emptyMotives", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.salesFilters.assignees", { defaultValue: "Responsável" })}
        selectedCount={userIds.length}
      >
        <DashboardMultiPickList
          options={users}
          value={userIds}
          onChange={setUserIds}
          getLabel={(o) => o?.nome || o?.name || o?.email || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.salesFilters.searchAssignees", {
            defaultValue: "Buscar responsáveis…",
          })}
          emptyLabel={t("dashboardPage.salesFilters.emptyAssignees", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.salesFilters.availability", { defaultValue: "Disponibilidade" })}
      >
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={availability}
          onChange={(e) => setAvailability(e.target.value)}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="">
            {t("dashboardPage.salesFilters.availabilityAll", { defaultValue: "Qualquer" })}
          </MenuItem>
          <MenuItem value="imediata">
            {t("dashboardPage.salesFilters.availabilityImmediate", {
              defaultValue: "Disponibilidade imediata",
            })}
          </MenuItem>
          <MenuItem value="sob-encomenda">
            {t("dashboardPage.salesFilters.availabilityOrder", {
              defaultValue: "Sob encomenda",
            })}
          </MenuItem>
        </Select>
      </DashboardFilterSection>

      {!loading ? (
        <DashboardSavedFiltersPanel
          section="vendas"
          getSnapshot={() =>
            normalizeSalesFilterState({
              ids_status: statusIds,
              ids_origens: originIds,
              ids_categorias: categoryIds,
              ids_motivos: motiveIds,
              ids_usuarios: userIds,
              availability,
              labels: buildLabels(),
            })
          }
          onApply={(state) => {
            onApply?.(state);
            onClose?.();
          }}
          canSave
        />
      ) : null}
    </DashboardFilterModalFrame>
  );
}
